import React, { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useNavigate } from 'react-router-dom';
import { rbacApi, RoleConfig } from '@/services/rbacApi';

const AVAILABLE_RESOURCES = [
  'products',
  'customers',
  'orders',
  'vendors',
  'couriers',
  'accounting',
  'extra-products',
  'taxonomy',
  'settings',
];

const AVAILABLE_ACTIONS = ['read', 'create', 'update', 'delete'];

const AVAILABLE_PAGES = [
  '/admin',
  '/admin/products',
  '/admin/customers',
  '/admin/orders',
  '/admin/vendors',
  '/admin/couriers',
  '/admin/extra-products',
  '/admin/accounting',
  '/admin/taxonomy',
  '/admin/delivery-areas',
  '/admin/coupons',
  '/admin/special-dates',
  '/admin/banners',
  '/admin/reviews',
  '/admin/pages',
  '/admin/payment-providers',
  '/admin/settings',
  '/admin/rbac-management',
  '/vendor/orders',
  '/vendor/couriers',
  '/vendor/profile',
];

const AVAILABLE_PAGES_BY_ROLE: Record<string, string[]> = {
  admin: AVAILABLE_PAGES.filter(page => page.startsWith('/admin')),
  vendor: AVAILABLE_PAGES.filter(page => page.startsWith('/vendor')),
};

const AVAILABLE_MENU_ITEMS = [
  'dashboard',
  'products',
  'customers',
  'orders',
  'vendors',
  'couriers',
  'extra-products',
  'delivery-areas',
  'accounting',
  'taxonomy',
  'coupons',
  'special-dates',
  'banners',
  'reviews',
  'pages',
  'payment-providers',
  'profile',
  'settings',
  'rbac-management',
];

const AVAILABLE_MENU_ITEMS_BY_ROLE: Record<string, string[]> = {
  admin: [...AVAILABLE_MENU_ITEMS],
  vendor: ['orders', 'couriers', 'profile'],
};

const DEFAULT_ROLE_CONFIGS: RoleConfig[] = [
  {
    role: 'admin',
    displayName: 'Admin',
    permissions: AVAILABLE_RESOURCES.reduce<Record<string, string[]>>((accumulator, resource) => {
      accumulator[resource] = [...AVAILABLE_ACTIONS];
      return accumulator;
    }, {}),
    pages: AVAILABLE_PAGES.filter(page => page.startsWith('/admin')),
    menuItems: [...AVAILABLE_MENU_ITEMS],
    description: 'Tam yetkili yönetici rolü',
  },
  {
    role: 'vendor',
    displayName: 'Vendor',
    permissions: {
      orders: ['read', 'update'],
      couriers: ['read', 'create'],
      vendors: ['read', 'update'],
    },
    pages: AVAILABLE_PAGES_BY_ROLE.vendor,
    menuItems: AVAILABLE_MENU_ITEMS_BY_ROLE.vendor,
    description: 'Vendor sipariş, kurye ve profil erişimi',
  },
];

const mergeRoleWithDefaults = (role: RoleConfig): RoleConfig => {
  const defaultRole = DEFAULT_ROLE_CONFIGS.find(item => item.role === role.role);

  if (!defaultRole) {
    return role;
  }

  return {
    ...defaultRole,
    ...role,
    permissions: Object.keys(defaultRole.permissions).reduce<Record<string, string[]>>((accumulator, resource) => {
      accumulator[resource] = role.permissions[resource] || defaultRole.permissions[resource] || [];
      return accumulator;
    }, { ...role.permissions }),
    pages: role.pages.length > 0 ? role.pages : defaultRole.pages,
    menuItems: role.menuItems.length > 0 ? role.menuItems : defaultRole.menuItems,
  };
};

export const RBACManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [roles, setRoles] = useState<RoleConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // 1️⃣ Admin check
  useEffect(() => {
    if (user && !['admin', 'super_admin'].includes(user.role || '')) {
      console.warn('Access denied: RBAC Management requires admin role');
      navigate('/unauthorized');
    }
  }, [user, navigate]);

  // 2️⃣ Load RBAC data
  useEffect(() => {
    const loadRBACData = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const firestoreRoles = await rbacApi.getRoles();

        const fallbackRoles = DEFAULT_ROLE_CONFIGS.filter(
          defaultRole => !firestoreRoles.some(role => role.role === defaultRole.role)
        );

        const rolesData = [...firestoreRoles.map(mergeRoleWithDefaults), ...fallbackRoles];

        setRoles(rolesData);
        if (rolesData.length > 0) {
          setSelectedRole(rolesData[0].role);
        }
      } catch (error) {
        console.error('❌ Failed to load RBAC data:', error);
        const fallbackRoles = DEFAULT_ROLE_CONFIGS.map(role => mergeRoleWithDefaults(role));
        setRoles(fallbackRoles);
        setSelectedRole(fallbackRoles[0]?.role || null);
        setLoadError('RBAC API erişilemedi. Varsayılan roller gösteriliyor.');
      } finally {
        setLoading(false);
      }
    };

    loadRBACData();
  }, []);

  const currentRole = roles.find(r => r.role === selectedRole);
  const availablePagesForRole = currentRole ? (AVAILABLE_PAGES_BY_ROLE[currentRole.role] || AVAILABLE_PAGES) : AVAILABLE_PAGES;
  const availableMenuItemsForRole = currentRole ? (AVAILABLE_MENU_ITEMS_BY_ROLE[currentRole.role] || AVAILABLE_MENU_ITEMS) : AVAILABLE_MENU_ITEMS;

  const handlePermissionToggle = (resource: string, action: string) => {
    if (!currentRole) return;

    const updatedRoles = roles.map(r => {
      if (r.role !== currentRole.role) return r;

      const updatedPermissions = { ...r.permissions };

      if (!updatedPermissions[resource]) {
        updatedPermissions[resource] = [];
      }

      const actionIndex = updatedPermissions[resource].indexOf(action);
      if (actionIndex > -1) {
        updatedPermissions[resource].splice(actionIndex, 1);
      } else {
        updatedPermissions[resource].push(action);
      }

      return { ...r, permissions: updatedPermissions };
    });

    setRoles(updatedRoles);
  };

  const handlePageToggle = (page: string) => {
    if (!currentRole) return;

    const updatedRoles = roles.map(r => {
      if (r.role !== currentRole.role) return r;

      const pages = r.pages.includes(page)
        ? r.pages.filter(p => p !== page)
        : [...r.pages, page];

      return { ...r, pages };
    });

    setRoles(updatedRoles);
  };

  const handleMenuItemToggle = (item: string) => {
    if (!currentRole) return;

    const updatedRoles = roles.map(r => {
      if (r.role !== currentRole.role) return r;

      const menuItems = r.menuItems.includes(item)
        ? r.menuItems.filter(m => m !== item)
        : [...r.menuItems, item];

      return { ...r, menuItems };
    });

    setRoles(updatedRoles);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updatedRoles = await rbacApi.updateRoles(roles);
      setRoles(updatedRoles.map(mergeRoleWithDefaults));
      console.log('✅ RBAC configuration saved successfully');
      alert('RBAC konfigürasyonu kaydedildi!');
    } catch (error) {
      console.error('❌ Failed to save RBAC configuration:', error);
      alert('Kayıt sırasında hata oluştu!');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!currentRole) {
    return <div className="p-6">Rol bulunamadı</div>;
  }

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="text-3xl font-bold mb-6">RBAC Yönetimi</h1>

      {loadError && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
          {loadError}
        </div>
      )}

      <div className="grid grid-cols-4 gap-6">
        {/* Role Selection */}
        <div className="col-span-1">
          <h2 className="text-lg font-semibold mb-4">Roller</h2>
          <div className="space-y-2">
            {roles.map(role => (
              <button
                key={role.role}
                onClick={() => setSelectedRole(role.role)}
                className={`w-full p-3 rounded text-left font-medium transition ${
                  selectedRole === role.role
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {role.displayName}
                {currentRole.role === 'admin' && <span className="text-xs ml-2">✓ Full Access</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Configuration */}
        <div className="col-span-3 space-y-6">
          {/* Permissions Matrix */}
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Kaynaklar ve İşlemler</h3>
            <div className="grid grid-cols-5 gap-4">
              <div className="font-bold">Kaynak</div>
              {AVAILABLE_ACTIONS.map(action => (
                <label key={action} className="font-bold text-sm">
                  {action}
                </label>
              ))}

              {AVAILABLE_RESOURCES.map(resource => (
                <React.Fragment key={resource}>
                  <div className="py-2">{resource}</div>
                  {AVAILABLE_ACTIONS.map(action => (
                    <label key={`${resource}-${action}`} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={currentRole.permissions[resource]?.includes(action) || false}
                        onChange={() => handlePermissionToggle(resource, action)}
                        disabled={currentRole.role === 'admin'}
                        className="w-4 h-4"
                      />
                    </label>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Pages Access */}
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Sayfa Erişimi</h3>
            <div className="grid grid-cols-2 gap-4">
              {availablePagesForRole.map(page => (
                <label key={page} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={currentRole.pages.includes(page)}
                    onChange={() => handlePageToggle(page)}
                    disabled={currentRole.role === 'admin'}
                    className="w-4 h-4"
                  />
                  <span>{page}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Menu Items */}
          <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Menü Öğeleri</h3>
            <div className="grid grid-cols-3 gap-4">
              {availableMenuItemsForRole.map(item => (
                <label key={item} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={currentRole.menuItems.includes(item)}
                    onChange={() => handleMenuItemToggle(item)}
                    disabled={currentRole.role === 'admin'}
                    className="w-4 h-4"
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex gap-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-green-500 text-white rounded font-medium hover:bg-green-600 disabled:bg-gray-500"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>
      </div>

      {currentRole.role === 'admin' && (
        <div className="mt-6 p-4 bg-blue-100 dark:bg-blue-900 rounded">
          <p className="text-blue-900 dark:text-blue-100">
            ℹ️ Admin rolünün tüm izinleri vardır ve değiştirilemez.
          </p>
        </div>
      )}
    </div>
  );
};
